<template>
  <Drawer
    v-model:visible="isVisible"
    :modal="false"
    position="right"
    :style="{ width: '42rem', maxWidth: '100vw' }"
    :pt="{
      content: { class: 'flex h-full flex-col p-0' }
    }"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <ClockIcon class="size-4 shrink-0 text-gray-400" aria-hidden="true" />
        <span class="font-semibold">Project Backups</span>
      </div>
    </template>

    <!-- Loading skeleton -->
    <div v-if="isLoading && versions.length === 0" class="space-y-5 p-6">
      <div v-for="i in 6" :key="i" class="flex gap-3">
        <Skeleton shape="circle" size="1.75rem" />
        <div class="flex-1 space-y-2">
          <Skeleton width="40%" height="0.875rem" />
          <Skeleton width="75%" height="0.875rem" />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!isLoading && versions.length === 0 && publishes.length === 0"
      class="flex flex-1 flex-col items-center justify-center p-8 text-center"
    >
      <ClockIcon class="mb-3 size-6 text-gray-300 dark:text-gray-600" aria-hidden="true" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-300">No backups yet.</p>
      <p class="mt-1 max-w-[38ch] text-sm text-gray-400">
        Snapshots are created automatically as you and your tools make changes to this project.
      </p>
    </div>

    <template v-else>
      <!-- Snapshot count + retention disclaimer -->
      <div class="shrink-0 border-b border-gray-950/5 px-6 py-3 dark:border-white/10">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          {{ showingSummary }}
        </p>
        <p class="mt-0.5 text-sm/5 text-gray-400">
          We keep your {{ retention.keep_count }} most recent snapshots, plus one per day from the
          past {{ retention.daily_days }} days. Older snapshots are removed automatically.
        </p>
      </div>

      <!-- Timeline -->
      <div
        ref="scrollContainer"
        class="min-h-0 flex-1 overflow-y-auto px-6 py-5"
        @scroll="handleScroll"
      >
        <div v-for="group in dayGroups" :key="group.key" class="mt-7 first:mt-0">
          <div class="text-xs font-semibold text-gray-400 dark:text-gray-500">
            {{ group.label }}
          </div>

          <ul role="list" class="mt-3">
            <li
              v-for="(entry, entryIndex) in group.entries"
              :key="entry.key"
              class="group relative flex gap-x-3 pb-6 last:pb-0"
            >
              <!-- Timeline rail -->
              <span
                v-if="entryIndex < group.entries.length - 1"
                class="absolute left-3.5 top-9 bottom-1 w-px bg-gray-950/5 dark:bg-white/10"
                aria-hidden="true"
              />

              <!-- Publish entry -->
              <template v-if="entry.kind === 'publish' && entry.publish">
                <div
                  class="flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 dark:bg-emerald-500"
                >
                  <RocketLaunchIcon class="size-4 text-white" aria-hidden="true" />
                </div>
                <div
                  class="-my-1.5 min-w-0 flex-1 rounded-lg bg-emerald-600/5 px-3 py-2 ring-1 ring-emerald-600/10 dark:bg-emerald-500/10 dark:ring-emerald-400/20"
                >
                  <div class="flex items-baseline justify-between gap-x-2">
                    <p class="flex min-w-0 items-baseline gap-x-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                      <span class="truncate">Published</span>
                      <span
                        v-if="entry.publish.is_live"
                        class="shrink-0 self-center rounded-full bg-emerald-600 px-1.5 py-0.5 text-[0.6875rem] font-medium text-white dark:bg-emerald-500"
                      >
                        Live
                      </span>
                    </p>
                    <div class="flex shrink-0 items-center gap-x-1.5">
                      <button
                        v-if="!entry.publish.is_live"
                        type="button"
                        class="flex items-center gap-x-1 rounded-md py-0.5 pl-1 pr-2 text-sm font-medium text-emerald-800 opacity-100 hover:bg-emerald-600/10 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100 dark:text-emerald-300 dark:hover:bg-emerald-400/10"
                        @click="confirmRestorePublish(entry.publish)"
                      >
                        <ArrowUturnLeftIcon class="size-4 shrink-0" aria-hidden="true" />
                        Restore
                      </button>
                      <span
                        class="text-xs text-gray-400 tabular-nums"
                        :title="formatDate(entry.publish.published_at)"
                      >
                        {{ formatTimeAgo(entry.publish.published_at) }}
                      </span>
                    </div>
                  </div>
                  <p
                    v-if="entry.publish.publish_message"
                    class="mt-1 text-sm text-gray-600 dark:text-gray-300"
                  >
                    {{ entry.publish.publish_message }}
                  </p>
                </div>
              </template>

              <!-- Snapshot entry -->
              <template v-else-if="entry.version">
                <!-- Avatar -->
                <img
                  v-if="entry.version.created_by?.email"
                  :src="gravatarUrl(entry.version.created_by.email)"
                  :alt="entry.version.created_by.name"
                  class="size-7 shrink-0 rounded-full bg-gray-50 ring-1 ring-inset ring-black/5 dark:bg-slate-800 dark:ring-white/10"
                >
                <div
                  v-else
                  class="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 ring-1 ring-inset ring-black/5 dark:bg-slate-800 dark:ring-white/10"
                >
                  <SparklesIcon
                    v-if="entry.version.source === 'mcp' || entry.version.source === 'righty'"
                    class="size-4 text-gray-400"
                    aria-hidden="true"
                  />
                  <UserIcon v-else class="size-4 text-gray-400" aria-hidden="true" />
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-x-2">
                    <p class="flex min-w-0 items-baseline gap-x-2 text-sm font-medium text-gray-900 dark:text-white">
                      <span class="truncate">{{ actorName(entry.version) }}</span>
                      <span
                        v-if="entry.version.id === latestVersionId"
                        class="shrink-0 self-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[0.6875rem] font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                      >
                        Latest
                      </span>
                    </p>
                    <div class="flex shrink-0 items-center gap-x-1.5">
                      <span class="flex items-center gap-x-1 opacity-100 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100">
                        <button
                          type="button"
                          class="rounded-md p-1 text-gray-400 hover:bg-gray-950/5 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200"
                          :title="expandedVersionId === entry.version.id ? 'Hide changed items' : 'Show changed items'"
                          @click="toggleDiff(entry.version)"
                        >
                          <ChevronDownIcon
                            class="size-4 shrink-0"
                            :class="{ 'rotate-180': expandedVersionId === entry.version.id }"
                            aria-hidden="true"
                          />
                          <span class="sr-only">
                            {{ expandedVersionId === entry.version.id ? 'Hide changed items' : 'Show changed items' }}
                          </span>
                        </button>
                        <button
                          type="button"
                          class="flex items-center gap-x-1 rounded-md py-0.5 pl-1 pr-2 text-sm font-medium text-gray-500 hover:bg-gray-950/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                          @click="confirmRestore(entry.version)"
                        >
                          <ArrowUturnLeftIcon class="size-4 shrink-0" aria-hidden="true" />
                          Restore
                        </button>
                      </span>
                      <span
                        class="text-xs text-gray-400 tabular-nums"
                        :title="formatDate(entry.version.created_at)"
                      >
                        {{ formatTimeAgo(entry.version.created_at) }}
                      </span>
                    </div>
                  </div>

                  <!-- AI-generated change description -->
                  <Skeleton
                    v-if="isGenerating(entry.version)"
                    class="mt-1.5"
                    width="85%"
                    height="0.875rem"
                  />
                  <p v-else class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                    {{ descriptionFor(entry.version) }}
                  </p>

                  <!-- Structured diff (expandable detail) -->
                  <div
                    v-if="expandedVersionId === entry.version.id"
                    class="mt-2 rounded-lg bg-gray-50 dark:bg-white/5"
                  >
                    <div v-if="loadingDiffId === entry.version.id" class="space-y-2 p-3">
                      <Skeleton width="50%" height="0.75rem" />
                      <Skeleton width="70%" height="0.75rem" />
                    </div>
                    <div
                      v-else-if="diffChanges && diffChanges.length === 0"
                      class="p-3 text-sm text-gray-400"
                    >
                      No differences detected.
                    </div>
                    <div v-else-if="diffChanges" class="divide-y divide-gray-950/5 dark:divide-white/10">
                      <div
                        v-for="(categoryChanges, category) in groupedDiffChanges"
                        :key="category"
                        class="px-3 py-2"
                      >
                        <div class="text-xs font-semibold text-gray-400 dark:text-gray-500">
                          {{ category }}
                        </div>
                        <ul role="list" class="mt-1 space-y-1">
                          <li
                            v-for="change in categoryChanges"
                            :key="change.id"
                            class="flex items-baseline gap-x-1.5 text-sm text-gray-700 dark:text-gray-300"
                          >
                            <PlusCircleIcon
                              v-if="change.type === 'added'"
                              class="size-4 shrink-0 self-center text-green-600 dark:text-green-400"
                              aria-hidden="true"
                            />
                            <MinusCircleIcon
                              v-else-if="change.type === 'deleted'"
                              class="size-4 shrink-0 self-center text-red-600 dark:text-red-400"
                              aria-hidden="true"
                            />
                            <PencilSquareIcon
                              v-else
                              class="size-4 shrink-0 self-center text-blue-600 dark:text-blue-400"
                              aria-hidden="true"
                            />
                            <span class="min-w-0 truncate">{{ change.name }}</span>
                            <span v-if="change.type !== 'modified'" class="shrink-0 text-xs text-gray-400 capitalize">
                              {{ change.type }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </li>
          </ul>
        </div>

        <!-- Load more spinner -->
        <div v-if="isLoadingMore" class="flex justify-center py-4">
          <ProgressSpinner style="width: 24px; height: 24px" stroke-width="6" />
        </div>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import type { ConfigPublishEntry, ConfigVersionEntry } from '@/services/configVersionService'
import { configVersionService } from '@/services/configVersionService'
import type { ConfigChange } from '@/composables/useConfigChanges'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { md5 } from 'js-md5'
import {
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ClockIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  RocketLaunchIcon,
  SparklesIcon,
  UserIcon
} from '@heroicons/vue/16/solid'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
})

const configStore = useConfigStore()
const { currentTeamPid } = storeToRefs(configStore)
const confirm = useConfirm()
const toast = useToast()
const { analyzeConfigOnlyChanges } = useConfigChanges()

// State
const versions = ref<ConfigVersionEntry[]>([])
const publishes = ref<ConfigPublishEntry[]>([])
const retention = ref({ keep_count: 50, daily_days: 30 })
const total = ref(0)
const isLoading = ref(false)
const isLoadingMore = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)

// Diff state
const expandedVersionId = ref<number | null>(null)
const loadingDiffId = ref<number | null>(null)
const diffChanges = ref<ConfigChange[] | null>(null)

// AI description state — cached for the session, persisted on the backend
const aiDescriptions = ref<Record<number, string>>({})
const generatingIds = ref<Set<number>>(new Set())
let describeQueue: number[] = []
let activeDescribes = 0
const MAX_CONCURRENT_DESCRIBES = 2

// Hide snapshots identical to the next-newer one (same checksum) — their
// "what changed" diff is empty, so they'd render as no-op entries. Mostly
// legacy data: the backend now dedupes these at creation time.
const displayedVersions = computed(() =>
  versions.value.filter(
    (version, index) => index === 0 || version.checksum !== versions.value[index - 1].checksum
  )
)

const latestVersionId = computed(() => displayedVersions.value[0]?.id ?? null)

const showingSummary = computed(() => {
  const count = displayedVersions.value.length
  const snapshots = `${count} saved snapshot${count === 1 ? '' : 's'}`
  const scope = versions.value.length < total.value ? `your last ${snapshots}` : `all ${snapshots}`
  const pubCount = displayedPublishes.value.length
  const pubs = pubCount > 0 ? ` and ${pubCount} publish${pubCount === 1 ? '' : 'es'}` : ''
  return `Showing ${scope}${pubs}.`
})

// Only interleave publishes that fall within the loaded snapshot range, so
// the infinite scroll doesn't show a publish "older" than unloaded snapshots
const displayedPublishes = computed(() => {
  if (publishes.value.length === 0) return []
  if (versions.value.length >= total.value) return publishes.value
  const oldestLoaded = versions.value[versions.value.length - 1]
  if (!oldestLoaded) return []
  const cutoff = new Date(oldestLoaded.created_at).getTime()
  return publishes.value.filter((p) => new Date(p.published_at).getTime() >= cutoff)
})

interface TimelineEntry {
  key: string
  kind: 'snapshot' | 'publish'
  date: Date
  version?: ConfigVersionEntry
  publish?: ConfigPublishEntry
}

const dayGroups = computed(() => {
  const entries: TimelineEntry[] = [
    ...displayedVersions.value.map((version) => ({
      key: `v-${version.id}`,
      kind: 'snapshot' as const,
      date: new Date(version.created_at),
      version
    })),
    ...displayedPublishes.value.map((publish) => ({
      key: `p-${publish.id}`,
      kind: 'publish' as const,
      date: new Date(publish.published_at),
      publish
    }))
  ].sort((a, b) => b.date.getTime() - a.date.getTime())

  const groups: { key: string; label: string; entries: TimelineEntry[] }[] = []
  for (const entry of entries) {
    const key = entry.date.toDateString()
    const group = groups[groups.length - 1]
    if (group && group.key === key) {
      group.entries.push(entry)
    } else {
      groups.push({ key, label: dayLabel(entry.date), entries: [entry] })
    }
  }
  return groups
})

const dayLabel = (date: Date) => {
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return 'Today'
  if (date.toDateString() === new Date(now.getTime() - 86400000).toDateString()) return 'Yesterday'
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {})
  })
}

const gravatarUrl = (email: string) =>
  `https://www.gravatar.com/avatar/${md5(email.toLowerCase().trim())}?s=64&d=mp`

const actorName = (version: ConfigVersionEntry) => {
  if (version.created_by?.name) return version.created_by.name
  switch (version.source) {
    case 'mcp': return 'AI agent (via MCP)'
    case 'righty': return 'Righty'
    case 'api': return 'API'
    default: return 'System'
  }
}

// Fetch versions
const fetchVersions = async (offset = 0) => {
  if (!currentTeamPid.value) return

  if (offset === 0) {
    isLoading.value = true
  } else {
    isLoadingMore.value = true
  }

  try {
    const result = await configVersionService.listVersions(currentTeamPid.value, offset)
    if (offset === 0) {
      versions.value = result.versions
      publishes.value = result.publishes ?? []
    } else {
      versions.value.push(...result.versions)
    }
    total.value = result.total
    if (result.retention) retention.value = result.retention
    queueDescriptions()
  } finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

// Load on open
watch(isVisible, (open) => {
  if (open) {
    expandedVersionId.value = null
    diffChanges.value = null
    fetchVersions(0)
  }
})

// Infinite scroll
const handleScroll = () => {
  if (!scrollContainer.value || isLoadingMore.value) return
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value
  if (scrollHeight - scrollTop - clientHeight < 100 && versions.value.length < total.value) {
    fetchVersions(versions.value.length)
  }
}

// AI descriptions — generated once on the backend (then stored on the version
// row), fetched here with limited concurrency for versions still missing one
const queueDescriptions = () => {
  for (const version of displayedVersions.value) {
    if (
      version.ai_description === null &&
      !(version.id in aiDescriptions.value) &&
      !generatingIds.value.has(version.id) &&
      !describeQueue.includes(version.id)
    ) {
      describeQueue.push(version.id)
    }
  }
  pumpDescribeQueue()
}

const pumpDescribeQueue = () => {
  while (activeDescribes < MAX_CONCURRENT_DESCRIBES && describeQueue.length > 0) {
    runDescribe(describeQueue.shift()!)
  }
}

const runDescribe = async (versionId: number) => {
  if (!currentTeamPid.value) return
  activeDescribes++
  generatingIds.value = new Set([...generatingIds.value, versionId])

  try {
    const result = await configVersionService.describeVersion(currentTeamPid.value, versionId)
    aiDescriptions.value[versionId] = result.description ?? ''
  } catch {
    aiDescriptions.value[versionId] = ''
  } finally {
    const next = new Set(generatingIds.value)
    next.delete(versionId)
    generatingIds.value = next
    activeDescribes--
    pumpDescribeQueue()
  }
}

const isGenerating = (version: ConfigVersionEntry) =>
  generatingIds.value.has(version.id) ||
  (version.ai_description === null &&
    !(version.id in aiDescriptions.value) &&
    describeQueue.includes(version.id))

const descriptionFor = (version: ConfigVersionEntry) => {
  const ai = version.ai_description ?? aiDescriptions.value[version.id]
  if (ai) return ai
  if (version.description) return version.description
  return fallbackDescription(version)
}

const fallbackDescription = (version: ConfigVersionEntry) => {
  switch (version.source_detail) {
    case 'save-draft': return 'Auto-saved while editing.'
    case 'publish':
    case 'publish-config': return 'Saved automatically before publishing.'
    case 'restore-version':
    case 'restore-publish': return 'Saved automatically before restoring a backup.'
    case 'undo': return 'Saved automatically before an undo.'
  }
  switch (version.source) {
    case 'mcp': return 'Changed by an AI agent connected via MCP.'
    case 'righty': return 'Changed by Righty, the AI assistant.'
    case 'api': return 'Changed via the API.'
    default: return 'Project snapshot.'
  }
}

// Time formatting — use plain JS to avoid creating orphaned VueUse watchers per call
const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr)
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
  return formatDate(dateStr)
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Structured diff toggle (secondary detail under the AI description)
const toggleDiff = async (version: ConfigVersionEntry) => {
  if (expandedVersionId.value === version.id) {
    expandedVersionId.value = null
    diffChanges.value = null
    return
  }

  if (!currentTeamPid.value) return

  expandedVersionId.value = version.id
  loadingDiffId.value = version.id
  diffChanges.value = null

  try {
    // Get this version's data
    const thisVersionData = await configVersionService.getVersionData(currentTeamPid.value, version.id)

    // Snapshots are taken before mutations, so "what changed in this entry"
    // is the diff against the next NEWER snapshot (or the current draft for
    // the latest one). Uses the displayed list so hidden no-op duplicates
    // aren't picked as the comparison target.
    const index = displayedVersions.value.findIndex((v) => v.id === version.id)
    let newerData: any
    if (index <= 0) {
      newerData = toRaw(configStore.config)
    } else {
      const newerVersion = displayedVersions.value[index - 1]
      const newerResult = await configVersionService.getVersionData(currentTeamPid.value, newerVersion.id)
      newerData = newerResult.data
    }

    // Config-only diff: the team-level appendages in analyzeChanges (split
    // tests, integrations, team settings) compare against the team's current
    // published state, which is unrelated to the two snapshots shown here.
    diffChanges.value = analyzeConfigOnlyChanges(newerData, thisVersionData.data)
  } catch (error) {
    console.error('Failed to load diff:', error)
    expandedVersionId.value = null
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load version diff', life: 3000 })
  } finally {
    loadingDiffId.value = null
  }
}

// Grouped diff changes (same pattern as PublishChangesModal)
const groupedDiffChanges = computed(() => {
  if (!diffChanges.value) return {}
  return diffChanges.value.reduce((acc, change) => {
    if (!acc[change.category]) {
      acc[change.category] = []
    }
    acc[change.category].push(change)
    return acc
  }, {} as Record<string, ConfigChange[]>)
})

// Restore a snapshot
const confirmRestore = (version: ConfigVersionEntry) => {
  confirm.require({
    message: `Your project will be restored to the state saved ${formatDate(version.created_at)}. A backup of the current state is created first, so you can undo this.`,
    header: 'Restore this backup?',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Restore',
    acceptClass: 'p-button-danger',
    accept: () => doRestore(version)
  })
}

const doRestore = async (version: ConfigVersionEntry) => {
  if (!currentTeamPid.value) return

  try {
    const result = await configVersionService.restoreVersion(currentTeamPid.value, version.id)
    await afterRestore(result.message)
  } catch (error) {
    console.error('Failed to restore version:', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to restore version', life: 3000 })
  }
}

// Restore a previous publish
const confirmRestorePublish = (publish: ConfigPublishEntry) => {
  confirm.require({
    message: `Your draft will be replaced with the version published ${formatDate(publish.published_at)}. Your live site isn't affected until you publish again. A backup of the current state is created first.`,
    header: 'Restore this published version?',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Restore',
    acceptClass: 'p-button-danger',
    accept: () => doRestorePublish(publish)
  })
}

const doRestorePublish = async (publish: ConfigPublishEntry) => {
  if (!currentTeamPid.value) return

  try {
    const result = await configVersionService.restorePublish(currentTeamPid.value, publish.id)
    await afterRestore(result.message)
  } catch (error) {
    console.error('Failed to restore publish:', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to restore published version', life: 3000 })
  }
}

const afterRestore = async (message: string) => {
  toast.add({ severity: 'success', summary: 'Restored', detail: message, life: 3000 })

  // Refetch config and version list. Route through the remoteRefetch
  // coordinator (ENG-1066) — a bare configQuery.refetch() races with a
  // pending debounced autosave, which could silently overwrite the
  // just-restored config.
  configStore.requestRemoteRefetch()
  await fetchVersions(0)

  expandedVersionId.value = null
  diffChanges.value = null
}
</script>
